<template>
  <router-view v-if="isStandaloneRoute" />
  <LoginScreen v-else-if="!authorized" @success="onAuthSuccess" />
  <OnboardingScreen v-else-if="showOnboarding" @complete="onOnboardingComplete" />
  <AppEntrySplash v-else>
    <a href="#appwidget_content" class="cg-skip-link">{{ skipLinkLabel }}</a>
    <div
      id="appwidget_content"
      class="min-h-dvh bg-[var(--sidebar-border)] px-4 text-foreground sm:px-6 md:min-h-screen md:px-8 lg:px-12 xl:px-16 2xl:px-[120px]"
      tabindex="-1"
    >
      <Navigation />

      <router-view v-slot="{ Component }">
        <Transition name="cg-screen" mode="out-in" appear>
          <div :key="String(route.name ?? route.path)" class="cg-screen-stage">
            <component :is="Component" />
          </div>
        </Transition>
      </router-view>
      <CgToastStack />
      <!--
  SVG recreation of the *layout* (background, shapes, icons, text).
  Earphones/product photo intentionally omitted.

  Notes:
  - Uses "Inter" if available, otherwise falls back to system sans-serif.
  - Colors/gradients are matched by eye to the reference.
  - You can tweak positions/sizes in the constants at the top-level <g id="layout">.

  Canvas: 900×1200
-->
    </div>
  </AppEntrySplash>
</template>

<script setup lang="ts">
import Navigation from "@/components/navigation/Navigation.vue";

import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ROUTE_NAMES } from "@/router";
import { getCardGeniusAccountProfile } from "./components/authorization/getCardGeniusAccountProfile";
import { AuthHttpError, hasAccessToken, onAuthExpired } from "./api/geniBackend";
import { linkGoogleIdToken } from "./api/authApi";
import { clearSocialCallbackUrl, parseSocialCallback, SOCIAL_OAUTH_CALLBACK_PATH } from "./api/socialOAuth";
import LoginScreen from "@/components/auth/LoginScreen.vue";
import OnboardingScreen from "@/pages/onboarding/OnboardingScreen.vue";
import { resetPosthog } from "@/lib/analytics";
import { markContentCreationOpenTrigger } from "@/lib/contentCreationWizardAnalytics";
import {
  buildOnboardingResolveInput,
  completeOnboarding,
  markOnboardingPending,
  shouldShowOnboarding,
} from "@/lib/onboarding";
import { trackMetaPixelPageView } from "@/lib/metaPixel";
import type { MarketplaceKey } from "@/marketplaces/registry";
import { useAuthStore } from "@/stores/auth";
import { useGoodAddingWizardStore } from "@/stores/goodAddingWizard";
import { useGoodsStore } from "./stores/goods";
import CgToastStack from "@/components/ui/toast/CgToastStack.vue";
import AppEntrySplash from "@/components/app/AppEntrySplash.vue";
import { useToastStore } from "@/stores/toast";
// import Test from "./pages/test/Test.vue";

const authorized = ref(hasAccessToken());
const showOnboarding = ref(false);
const goodsStore = useGoodsStore();
const toastStore = useToastStore();
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const goodAddingWizardStore = useGoodAddingWizardStore();

const isStandaloneRoute = computed(() => Boolean(route.meta?.standalone));

function resolveOnboardingInput(registrationPending?: boolean) {
  return buildOnboardingResolveInput({
    userId: authStore.cardGeniusAccountProfile?.userId,
    onboardingCompletedAt: authStore.onboardingCompletedAt,
    registrationPending,
    goodsCount: goodsStore.goodsCount,
  });
}

function applyOnboardingGate(registrationPending?: boolean): boolean {
  if (!shouldShowOnboarding(resolveOnboardingInput(registrationPending))) {
    return false;
  }
  showOnboarding.value = true;
  if (typeof document !== "undefined") {
    document.title = "CardGenius — Добро пожаловать";
  }
  return true;
}

const unsubscribeAuthExpired = onAuthExpired(() => {
  resetPosthog();
  authorized.value = false;
  showOnboarding.value = false;
  // Avoid leaving /settings (or other tabs) in the address bar behind LoginScreen.
  if (route.name === ROUTE_NAMES.settings) {
    void router.replace({ name: ROUTE_NAMES.goods });
  }
});

async function onAuthSuccess(payload?: { fromRegistration?: boolean }) {
  authorized.value = true;
  if (payload?.fromRegistration) {
    markOnboardingPending();
  }
  const bootstrapped = await bootstrapAuthorizedApp();
  if (!bootstrapped) return;

  if (applyOnboardingGate(payload?.fromRegistration)) {
    return;
  }

  // Google OAuth clears the URL to "/" (clearSocialCallbackUrl); phone/WhatsApp login keeps
  // the current path, so a session that expired on /settings re-opens Settings after login.
  if (route.name !== ROUTE_NAMES.goods) {
    await router.replace({ name: ROUTE_NAMES.goods });
  }

  trackMetaPixelPageView();
  hasTrackedAuthorizedTabPageView = true;
}

async function onOnboardingComplete(marketplace: MarketplaceKey) {
  const userId = authStore.cardGeniusAccountProfile?.userId;
  const completedAt = await completeOnboarding(userId);
  if (completedAt) {
    authStore.setOnboardingCompletedAt(completedAt);
  }
  if (marketplace !== "generic") {
    await authStore.setPreferredMarketplace(marketplace);
  }

  markContentCreationOpenTrigger("onboarding");

  await router.push({
    name: ROUTE_NAMES.goodNew,
    query: { fresh: "1", mp: marketplace },
  });

  showOnboarding.value = false;

  trackMetaPixelPageView();
  hasTrackedAuthorizedTabPageView = true;
}

async function bootstrapAuthorizedApp(): Promise<boolean> {
  if (
    typeof window !== "undefined" &&
    window.location.pathname === SOCIAL_OAUTH_CALLBACK_PATH &&
    hasAccessToken()
  ) {
    const parsed = parseSocialCallback();
    if (parsed && !("error" in parsed) && parsed.mode === "link") {
      try {
        await linkGoogleIdToken(parsed.idToken);
        toastStore.push({
          tone: "success",
          state: "done",
          title: "Google привязан к аккаунту",
        });
      } catch (e) {
        console.error(e);
        toastStore.push({
          tone: "error",
          state: "done",
          title: e instanceof Error ? e.message : "Не удалось привязать Google",
        });
      } finally {
        clearSocialCallbackUrl();
      }
    } else if (parsed && "error" in parsed) {
      clearSocialCallbackUrl();
    } else if (parsed && !("error" in parsed) && parsed.mode !== "link") {
      clearSocialCallbackUrl();
    }
  }

  try {
    await getCardGeniusAccountProfile();
    await goodsStore.init();
    return true;
  } catch (error) {
    if (error instanceof AuthHttpError && error.status === 401) {
      authorized.value = false;
      return false;
    }
    throw error;
  }
}

async function initAuthorizedSession() {
  const bootstrapped = await bootstrapAuthorizedApp();
  if (!bootstrapped) return;

  applyOnboardingGate();
}

if (authorized.value) {
  initAuthorizedSession().catch((error) => {
    console.error("[App] initAuthorizedSession failed", error);
  });
}

onBeforeUnmount(() => {
  unsubscribeAuthExpired();
});

const skipLinkLabel = "Перейти к содержимому";

let hasTrackedAuthorizedTabPageView = false;

watch(
  () => route.name,
  (name) => {
    if (!name) return;
    if (showOnboarding.value) return;
    if (isStandaloneRoute.value) return;

    if (!authorized.value) return;

    if (!hasTrackedAuthorizedTabPageView) {
      hasTrackedAuthorizedTabPageView = true;
      return;
    }

    trackMetaPixelPageView();
  },
  { immediate: true }
);
</script>

<style lang="scss">
.font {
  font-family:
    Inter,
    system-ui,
    -apple-system,
    Segoe UI,
    Roboto,
    Arial,
    sans-serif;
}
.bold {
  font-weight: 800;
}
.black {
  font-weight: 900;
}
.tight {
  letter-spacing: -0.02em;
}
.titleBlue {
  fill: url(#titleBlue);
}
.titleBlack {
  fill: #101010;
}
.white {
  fill: #ffffff;
}
.blue {
  fill: #0a68ff;
}
.green {
  fill: #34e61c;
}
.dark {
  fill: #0b0b0b;
}
.muted {
  fill: #1a1a1a;
  opacity: 0.75;
}

.not-fullscreen__content {
  .modal-body {
    padding: 0;
    height: calc(100% - 88px) !important;

    // position: absolute !important;
    // top: 0 !important;
    // left: 0 !important;
    // right: 0 !important;
    // bottom: 0 !important;
    // height: auto !important;
  }
}

.cg-screen-enter-active,
.cg-screen-leave-active {
  transition:
    opacity 220ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 220ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: opacity, transform;
}

.cg-screen-enter-from,
.cg-screen-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

.cg-screen-stage {
  width: 100%;
  contain: layout paint;
}

/* Lightweight staged reveal for page blocks. */
.cg-screen-stage > * {
  animation: cg-block-enter 260ms cubic-bezier(0.22, 1, 0.36, 1) both;
}

.cg-screen-stage > * > * {
  animation: cg-block-enter 300ms cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 28ms;
}

@keyframes cg-block-enter {
  from {
    opacity: 0.001;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .cg-screen-enter-active,
  .cg-screen-leave-active {
    transition: none !important;
  }

  .cg-screen-stage > *,
  .cg-screen-stage > * > * {
    animation: none !important;
  }
}

/* WCAG 2.4.1 skip link — visible on keyboard focus */
.cg-skip-link {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  z-index: 100000;
}

.cg-skip-link:focus,
.cg-skip-link:focus-visible {
  position: fixed;
  left: 12px;
  top: 12px;
  width: auto;
  height: auto;
  margin: 0;
  padding: 10px 14px;
  overflow: visible;
  clip: auto;
  white-space: normal;
  background: var(--card);
  color: var(--foreground);
  border: 2px solid var(--ring);
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
  text-decoration: none;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
}
</style>
